/*
 * Risk ratings (inherent and residual), shared by TPRM and UCRM, and their display helpers and filter options.
 */
import { IndicatorVariant } from 'Components/Indicator/Indicator';

import { GroupOptionType, OptionType } from './Types/GlobalType';

// Note: It would likely be better if "UNRATED" were not a member of this enum, and instead, `undefined` represented absent risk ratings. It's confusing and error-prone that we can equate ratings to numbers _except_ for when a rating is "unrated": for example, special care has to be taken in some places to completely ignore `0` ratings, such as when the backend calculates averages of "rated" services for Dashboards. If it's easy to change this in the future, go for it; otherwise, it doesn't seem worth the time/effort.
export enum RiskRating {
    UNRATED = 0,
    LOW,
    LOW_MODERATE,
    MODERATE,
    MODERATE_HIGH,
    HIGH,
}

export const numberAsRiskScore = (riskScore: number): RiskRating => {
    if (riskScore === 0) {
        return RiskRating.UNRATED;
    } else if (riskScore >= 1 && riskScore < 2) {
        return RiskRating.LOW;
    } else if (riskScore >= 2 && riskScore < 3) {
        return RiskRating.LOW_MODERATE;
    } else if (riskScore >= 3 && riskScore < 4) {
        return RiskRating.MODERATE;
    } else if (riskScore >= 4 && riskScore < 5) {
        return RiskRating.MODERATE_HIGH;
    } else if (riskScore === 5) {
        return RiskRating.HIGH;
    }

    return RiskRating.UNRATED;
};

export const riskRatingAsString = (riskRating: RiskRating): string => {
    switch (riskRating) {
        case RiskRating.UNRATED:
            return 'Unrated';
        case RiskRating.HIGH:
            return 'High';
        case RiskRating.MODERATE_HIGH:
            return 'Moderate/High';
        case RiskRating.MODERATE:
            return 'Moderate';
        case RiskRating.LOW_MODERATE:
            return 'Low/Moderate';
        case RiskRating.LOW:
            return 'Low';
    }
};

export const numberAsRiskRatingString = (riskRating: number): string => {
    return riskRatingAsString(numberAsRiskScore(riskRating));
};

export const riskRatingLabelAsNumber = (riskRatingLabel: string) => {
    switch (riskRatingLabel) {
        case 'Unrated':
            return RiskRating.UNRATED;
        case 'High':
            return RiskRating.HIGH;
        case 'Moderate/High':
            return RiskRating.MODERATE_HIGH;
        case 'Moderate':
            return RiskRating.MODERATE;
        case 'Low/Moderate':
            return RiskRating.LOW_MODERATE;
        case 'Low':
            return RiskRating.LOW;
    }
};

export const getRiskRatingVariantColor = (riskRating: RiskRating): IndicatorVariant => {
    switch (riskRating) {
        case RiskRating.UNRATED:
            return IndicatorVariant.GRAY;
        case RiskRating.LOW:
            return IndicatorVariant.DARK_GREEN;
        case RiskRating.LOW_MODERATE:
            return IndicatorVariant.LIGHT_GREEN;
        case RiskRating.MODERATE:
            return IndicatorVariant.YELLOW;
        case RiskRating.MODERATE_HIGH:
            return IndicatorVariant.ORANGE;
        case RiskRating.HIGH:
            return IndicatorVariant.RED;
    }
};

const INHERENT_RISK_GROUP_ID = 'inherent_risk_score';

const RESIDUAL_RISK_GROUP_ID = 'residual_risk_score';

export const InherentRiskFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Unrated',
        value: RiskRating.UNRATED,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: High',
        value: RiskRating.HIGH,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Moderate/High',
        value: RiskRating.MODERATE_HIGH,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Moderate',
        value: RiskRating.MODERATE,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Low/Moderate',
        value: RiskRating.LOW_MODERATE,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Low',
        value: RiskRating.LOW,
    },
];

export const ResidualRiskFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Unrated',
        value: RiskRating.UNRATED,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: High',
        value: RiskRating.HIGH,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Moderate/High',
        value: RiskRating.MODERATE_HIGH,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Moderate',
        value: RiskRating.MODERATE,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Low/Moderate',
        value: RiskRating.LOW_MODERATE,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Low',
        value: RiskRating.LOW,
    },
];

export const RiskRatingSelectOptions: OptionType[] = [
    {
        label: 'Low',
        value: RiskRating.LOW,
    },
    {
        label: 'Low/Moderate',
        value: RiskRating.LOW_MODERATE,
    },
    {
        label: 'Moderate',
        value: RiskRating.MODERATE,
    },
    {
        label: 'Moderate/High',
        value: RiskRating.MODERATE_HIGH,
    },
    {
        label: 'High',
        value: RiskRating.HIGH,
    },
];
